import { Component } from 'preact';
import RuneSpinner from './RuneSpinner';
import { IconDiscord } from './ui/icons';
import {
  BG_ATMOSPHERE, VIGNETTE, TEXT_PRIMARY, TEXT_MUTED, TEXT_BODY,
  DIALOG_STYLE, GOLD_BTN, BEVELED_BTN, INPUT_STYLE, ACCENT_GOLD,
  FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { t } from '../localization/t';

// Mirrors the server validator in valkenhall-server/src/routes/profile.js
// and the existing live check in SettingsScreen.
const DISCORD_HANDLE_PATTERN = /^[a-z0-9_.]{2,32}$/;

const ERROR_COLOR = '#c45050';

function normalizeHandle(raw) {
  return String(raw || '').trim().toLowerCase().replace(/^@+/, '');
}

export default class ArenaDiscordPrompt extends Component {
  constructor(props) {
    super(props);
    this.state = {
      handle: '',
      localError: null,
      serverError: null,
      saving: false,
    };
  }

  handleInput = (e) => {
    this.setState({
      handle: e.target.value,
      localError: null,
      serverError: null,
    });
  };

  handleSave = async () => {
    const cleaned = normalizeHandle(this.state.handle);
    if (cleaned === '') {
      this.setState({ localError: t('Enter a handle or tap Skip') });
      return;
    }
    if (!DISCORD_HANDLE_PATTERN.test(cleaned)) {
      this.setState({
        localError: t('2–32 chars, lowercase letters, digits, underscores or dots'),
      });
      return;
    }
    this.setState({ saving: true, localError: null, serverError: null });
    try {
      await this.props.onSave(cleaned);
    } catch (err) {
      this.setState({ saving: false, serverError: err.message });
    }
  };

  handleSkip = () => {
    if (this.state.saving) return;
    this.props.onSkip();
  };

  render() {
    const { handle, localError, serverError, saving } = this.state;
    const cleaned = normalizeHandle(handle);
    const valid = cleaned !== '' && DISCORD_HANDLE_PATTERN.test(cleaned);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: BG_ATMOSPHERE }}>
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative w-full max-w-[30cqw] px-6">
          <div className="relative p-8" style={DIALOG_STYLE}>
            <FourCorners />

            <div className="text-center mb-6">
              <div
                className="mx-auto mb-3 flex items-center justify-center"
                style={{
                  width: '3cqw',
                  height: '3cqw',
                  color: ACCENT_GOLD,
                }}
              >
                <IconDiscord size="3cqw" />
              </div>
              <h1
                className="text-2xl font-bold arena-heading mb-2"
                style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(200,160,60,0.1)' }}
              >
                {t('Connect on Discord')}
              </h1>
              <p className="text-sm" style={{ color: TEXT_BODY }}>
                {t('Add your Discord handle so other players, friends, and tournament organizers can reach you.')}
              </p>
              <p className="text-xs mt-1" style={{ color: TEXT_MUTED }}>
                {t('Optional. You can add or change this anytime in Settings.')}
              </p>
            </div>

            <OrnamentalDivider className="mb-6" />

            <div className="flex flex-col gap-4">
              <div className="relative">
                <span
                  className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{
                    color: TEXT_MUTED,
                    fontSize: '1cqw',
                    fontWeight: 600,
                  }}
                >
                  @
                </span>
                <input
                  type="text"
                  value={handle}
                  maxLength={32}
                  placeholder={t('your_discord_handle')}
                  className="w-full pl-7 pr-4 py-3 text-lg outline-none"
                  style={{
                    ...INPUT_STYLE,
                    borderRadius: 'var(--radius-lg)',
                    color: TEXT_PRIMARY,
                    fontSize: '1cqw',
                    letterSpacing: '0.02em',
                  }}
                  onInput={this.handleInput}
                  onKeyDown={(e) => { if (e.key === 'Enter') this.handleSave(); }}
                  autoFocus
                  spellcheck={false}
                  autocomplete="off"
                  autocapitalize="off"
                />
              </div>

              {localError || serverError ? (
                <div className="text-center text-sm" style={{ color: ERROR_COLOR }}>
                  {localError || serverError}
                </div>
              ) : null}

              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={saving}
                  className="flex-1 py-3 text-sm font-semibold arena-heading cursor-pointer transition-all disabled:cursor-not-allowed"
                  style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                  onClick={this.handleSkip}
                >
                  {t('Skip for now')}
                </button>
                <button
                  type="button"
                  disabled={!valid || saving}
                  className="flex-1 py-3 text-sm font-semibold arena-heading cursor-pointer transition-all disabled:cursor-not-allowed"
                  style={saving || valid ? GOLD_BTN : { ...BEVELED_BTN, color: TEXT_MUTED, borderRadius: 'var(--radius-md)', opacity: 0.3 }}
                  onMouseEnter={(e) => {
                    if (valid && !saving) {
                      e.currentTarget.style.transform = 'scale(1.03)';
                      e.currentTarget.style.boxShadow = '0 0 30px rgba(212,168,67,0.3), inset 0 1px 0 rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (valid && !saving) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = GOLD_BTN.boxShadow;
                    }
                  }}
                  onClick={this.handleSave}
                >
                  {saving ? (
                    <span className="relative">
                      <span className="invisible">{t('Save Handle')}</span>
                      <span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={20} /></span>
                    </span>
                  ) : t('Save Handle')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
